import { Button, Icon } from "@sellar-io/components";
import { OrderBanner } from "../deliveryPreferences/components/OrderBanner";
import type { OrderLinkFlowGetResponseType } from "@sellar-io/api-v2/types";
import { useNavigate } from "react-router";
import { useState } from "react";
import { useIntercom } from "react-use-intercom";
import { ORDER_DETAILS } from "~/constants/routes";

interface RestrictedAccessScreenProps {
  order: OrderLinkFlowGetResponseType;
}

export function RestrictedAccessScreen({ order }: RestrictedAccessScreenProps) {
  const navigate = useNavigate();
  const intercom = useIntercom();
  const [showPermissionView, setShowPermissionView] = useState(false);

  const handleLogin = () => {
    const orderUrl = `${ORDER_DETAILS.href({ orderId: order.orderId })}?tab=All+orders`;
    navigate(orderUrl);
  };

  const handleNotMyAccountClick = () => {
    setShowPermissionView(true);
  };

  const handleContactSupport = () => {
    intercom.show();
  };

  return (
    <div className="flex min-h-screen flex-col bg-neutral-100">
      {/* Order Banner */}
      <div className="w-full bg-white">
        <OrderBanner order={order} />
      </div>

      {/* Restricted Access Content */}
      <div className="flex-1 px-5 py-8">
        <div className="mx-auto w-full max-w-md">
          {showPermissionView ? (
            <div className="text-center">
              <div className="mb-6">
                <h1 className="text-left text-xl font-semibold text-zinc-800">
                  You need to get permission to join {order.retailerName}'s
                  Sellar account
                </h1>
                <p className="mt-3 text-left text-sm text-zinc-600">
                  You can ask the member of your team who has claimed the
                  account to add you as a user. Alternatively, you can contact
                  Sellar support to request access.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  color="secondary"
                  className="w-full"
                  onClick={handleContactSupport}
                >
                  CONTACT SUPPORT
                </Button>
              </div>

              <div className="mt-4 text-center">
                <p className="text-sm text-zinc-600">
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={handleLogin}
                    className="text-blue-600 hover:underline"
                  >
                    Log in
                  </button>
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <div className="mb-6">
                <h1 className="text-left text-xl font-semibold text-zinc-800">
                  The {order.retailerName}'s Sellar account already has an
                  active user associated with it
                </h1>
                <p className="mt-3 text-left text-sm text-zinc-600">
                  If this is you, please log in to edit your order and delivery
                  preferences
                </p>
              </div>

              {/* Active Users List */}
              {order.retailerBusinessUsers.length > 0 && (
                <div className="mb-6 text-left">
                  <p className="mb-3 text-sm font-medium text-zinc-700">
                    Active user
                  </p>
                  <div className="space-y-2">
                    {order.retailerBusinessUsers.map((user, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-white p-4"
                      >
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100">
                          <Icon
                            icon="ACCOUNT_CIRCLE"
                            size={1.2}
                            className="text-zinc-500"
                          />
                        </div>
                        <div className="flex flex-col text-left">
                          <p className="text-sm font-semibold text-zinc-800">
                            {user.obfuscatedName}
                          </p>
                          <p className="text-sm text-zinc-600">
                            {user.obfuscatedEmail}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button className="w-full" onClick={handleLogin}>
                  LOG IN TO EDIT ORDER
                  <Icon icon="CHEVRON_RIGHT" size={0.9} />
                </Button>

                <Button
                  color="secondary"
                  className="w-full"
                  onClick={handleNotMyAccountClick}
                >
                  THIS ISN'T MY USER ACCOUNT
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
